// A department head (admin / department admin / manager / team lead) gets two
// dashboards: "My work" — the same dashboard their team uses, for their own
// leads, quotations, follow-ups and visits — and "My team": each team member's
// numbers for a chosen period, today's attendance, and the team's pipeline.
// Team numbers: GET /api/dashboard-kpi/team (src/api-dashboard-kpi/owner.js).

import { useMemo, useState } from 'react';
import { useRememberedState } from '@/shared/hooks/useRememberedFilters';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Users, UserRound, FileText, Radio, Receipt, Wallet, FileSpreadsheet, Target, AlarmClock } from 'lucide-react';
import { WelcomeBanner, StatTile, type BannerStat } from '../infrastructure/widgets/WelcomeBanner';
import { useCollapsedList, ShowAllButton } from '../infrastructure/widgets/DashboardKit';
import { api } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { formatINR } from '@/shared/utils/currency';
import { useTeamAttendance } from '@/features/attendance/hooks/useAttendance';
import { formatClock, formatDuration, minutesOnDuty } from '@/features/attendance/utils';
import { SalesEmployeeDashboard } from './SalesEmployeeDashboard';
import { SalesAdminDashboard } from './SalesAdminDashboard';
import { EmployeePipelineMonitor, canMonitorPipeline } from '@/features/pipeline/components/EmployeePipelineMonitor';
import { usePipelineRepSummary } from '@/features/pipeline/hooks/usePipeline';
import { AsmTeamDsrCard } from '../widgets/sales-employee/AsmTeamDsrCard';
import { BdmDsrWidget } from '../widgets/sales-employee/BdmDsrWidget';
import { LiveFleetRadarPage } from '@/features/tracking/pages/LiveFleetRadarPage';
import { WidgetEmpty, WidgetError, WidgetLoading } from '../infrastructure/widgets/WidgetStates';

interface TeamRow {
  userId: string; name: string; role: string;
  designation?: string; phone?: string;
  leadsNew: number; leadsOpen: number; pipelineValue: number;
  quotations: number; quotationValue: number;
  invoices: number; sales: number; collected: number;
  overdueFollowups: number; monthTarget: number; lastSeenAt: string | null;
}

const dayKey = (d: Date) => d.toLocaleDateString('en-CA');
const PERIODS = [
  { key: 'today', label: 'Today', range: () => { const t = dayKey(new Date()); return [t, t] as const; } },
  { key: 'week', label: 'This week', range: () => { const n = new Date(); const s = new Date(n); s.setDate(n.getDate() - ((n.getDay() + 6) % 7)); return [dayKey(s), dayKey(n)] as const; } },
  { key: 'month', label: 'This month', range: () => { const n = new Date(); return [dayKey(new Date(n.getFullYear(), n.getMonth(), 1)), dayKey(n)] as const; } },
  { key: 'last_month', label: 'Last month', range: () => { const n = new Date(); return [dayKey(new Date(n.getFullYear(), n.getMonth() - 1, 1)), dayKey(new Date(n.getFullYear(), n.getMonth(), 0))] as const; } },
];
const ago = (iso: string | null) => {
  if (!iso) return 'never';
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  return h < 1 ? 'within the hour' : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

type TeamTotals = { leadsNew: number; quotations: number; sales: number; collected: number; overdue: number };
const sumTeam = (team: TeamRow[]): TeamTotals => team.reduce((t, r) => ({
  leadsNew: t.leadsNew + r.leadsNew, quotations: t.quotations + r.quotations, sales: t.sales + r.sales,
  collected: t.collected + r.collected, overdue: t.overdue + r.overdueFollowups,
}), { leadsNew: 0, quotations: 0, sales: 0, collected: 0, overdue: 0 });

/** The team's five figures, the same in the banner and on the My team tab. */
function teamStats(t: TeamTotals): BannerStat[] {
  return [
    { label: 'Invoiced', value: formatINR(t.sales), icon: Receipt, tone: 'primary' },
    { label: 'Collected', value: formatINR(t.collected), icon: Wallet, tone: 'success' },
    { label: 'Quotations', value: String(t.quotations), icon: FileSpreadsheet, tone: 'info' },
    { label: 'New leads', value: String(t.leadsNew), icon: Target, tone: 'neutral' },
    { label: 'Overdue follow-ups', value: String(t.overdue), icon: AlarmClock, tone: t.overdue ? 'danger' : 'success' },
  ];
}

function TeamView() {
  const user = useUser();
  const [period, setPeriod] = useState('month');
  const [from, to] = (PERIODS.find((p) => p.key === period) ?? PERIODS[2]).range();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'team', from, to],
    queryFn: async () => (await api.get<{ team: TeamRow[] | null; department: string }>('/api/dashboard-kpi/team', { params: { from, to } })).data,
    placeholderData: keepPreviousData,
  });
  const { data: attendance = [], isError: attendanceHidden } = useTeamAttendance(true);
  // Checklist item 46 — "Admin Dashboard: Sales Pipeline Monitor". The monitor
  // already existed, but only on the pipeline page; this is the screen a
  // department head actually opens in the morning, so the monitor lives here
  // too. Same period the team table is showing, so the two cannot disagree.
  const monitorAllowed = canMonitorPipeline(user?.role);
  const [monitorOwner, setMonitorOwner] = useState('');
  const { data: repSummary, isLoading: repSummaryLoading } = usePipelineRepSummary(
    { startDate: from, endDate: to },
    monitorAllowed,
  );
  const team = data?.team ?? [];
  const totals = useMemo(() => sumTeam(team), [team]);
  const inNow = attendance.filter((a) => a.check_in && !a.check_out).length;
  // In now first, then checked out, then not in — and only the first rows open.
  const attendanceSorted = useMemo(() => [...attendance].sort((a, b) =>
    (a.check_in ? (a.check_out ? 1 : 0) : 2) - (b.check_in ? (b.check_out ? 1 : 0) : 2)), [attendance]);
  const attList = useCollapsedList(attendanceSorted, 8);

  const card = 'overflow-hidden rounded-card border border-border bg-card shadow-surface-1';
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-body-sm text-on-surface-variant">Your {data?.department || user?.department || ''} team · {from === to ? from : `${from} to ${to}`}</p>
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          {PERIODS.map((p) => (
            <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
              className={`rounded-lg px-2.5 py-1.5 text-body-sm font-semibold ${period === p.key ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-variant'}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {monitorAllowed && (
        <EmployeePipelineMonitor
          summaryData={repSummary}
          isLoading={repSummaryLoading}
          selectedOwnerId={monitorOwner}
          onSelectOwner={setMonitorOwner}
          userRole={user?.role}
        />
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {teamStats(totals).map((stat) => <StatTile key={stat.label} stat={stat} />)}
      </div>

      <section className={card}>
        <div className="border-b border-border bg-surface-container-low/60 px-5 py-3"><h3 className="text-body-md font-semibold text-on-surface">Team scoreboard</h3></div>
        <div className="max-h-[560px] overflow-auto p-4">
          {isLoading ? <WidgetLoading variant="table" />
            : isError || data?.team === null ? <WidgetError message="The team numbers could not be read for this period." />
            : team.length === 0 ? <WidgetEmpty message={`No one has reported against ${data?.department || 'your department'} in this window. Widen the period, or check the team is mapped to your department.`} /> : (
              <table className="w-full text-body-sm">
                <thead className="bg-muted">
                  <tr className="text-left text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <th scope="col" className="sticky top-0 z-10 bg-muted py-2 pr-2">Member</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">New leads</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Open leads</th>
                    <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Quotations</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Invoiced</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Collected</th>
                    <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Target</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Overdue follow-ups</th><th scope="col" className="sticky top-0 z-10 bg-muted py-2 pl-2 text-right">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((r) => {
                    const pct = r.monthTarget > 0 ? Math.round((r.sales / r.monthTarget) * 100) : null;
                    return (
                      <tr key={r.userId} className="border-b border-border">
                        <td className="py-2 pr-2 font-semibold text-on-surface">
                          <div className="flex items-center gap-1.5">
                            <span>{r.name}</span>
                            {r.userId === String(user?.id) && <span className="text-data-xs text-on-surface-subtle font-normal">(you)</span>}
                          </div>
                          {(r.designation || r.phone) && (
                            <div className="flex flex-wrap items-center gap-2 text-data-xs text-on-surface-variant font-normal">
                              {r.designation && <span>{r.designation}</span>}
                              {r.phone && (
                                <a
                                  href={`tel:${r.phone}`}
                                  className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
                                  title={`Call ${r.name}: ${r.phone}`}
                                >
                                  <span>📞</span>
                                  <span>{r.phone}</span>
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-data-sm">{r.leadsNew}</td>
                        <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{r.leadsOpen} <span className="text-on-surface-subtle">· {formatINR(r.pipelineValue)}</span></td>
                        <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{r.quotations} <span className="text-on-surface-subtle">· {formatINR(r.quotationValue)}</span></td>
                        <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-primary-dim text-data-sm">{formatINR(r.sales)}</td>
                        <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-success-text text-data-sm">{formatINR(r.collected)}</td>
                        <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{pct === null ? <span className="text-on-surface-subtle">not set</span> : `${pct}%`}</td>
                        <td className={`px-2 py-2 text-right font-semibold tabular-nums ${r.overdueFollowups ? 'text-danger-text' : 'text-on-surface-subtle'}`}>{r.overdueFollowups}</td>
                        <td className="font-mono py-2 pl-2 text-right text-on-surface-variant tabular-nums text-data-sm">{ago(r.lastSeenAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
      </section>

      {!attendanceHidden && (
        <section className={card}>
          <div className="flex items-center justify-between border-b border-border bg-surface-container-low/60 px-5 py-3">
            <h3 className="text-body-md font-semibold text-on-surface">Attendance today</h3>
            <span className="text-body-sm font-semibold text-success-text">{inNow} in now · {attendance.filter((a) => !a.check_in).length} not checked in</span>
          </div>
          <div className="max-h-[560px] overflow-auto p-4">
            <table className="w-full text-body-sm">
              <thead className="bg-muted">
                <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                  <th className="py-2 pr-2">Member</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2">In</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2">Out</th><th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2 text-right">Worked</th><th scope="col" className="sticky top-0 z-10 bg-muted py-2 pl-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {attList.visible.map((a) => (
                  <tr key={a.id} className="border-b border-border">
                    <td className="py-2 pr-2 font-semibold text-on-surface">{a.display_name}</td>
                    <td className="px-2 py-2 tabular-nums">{a.check_in ? formatClock(a.check_in) : '—'}</td>
                    <td className="px-2 py-2 tabular-nums">{a.check_out ? formatClock(a.check_out) : '—'}</td>
                    <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{a.check_in ? formatDuration(minutesOnDuty(a.check_in, a.check_out)) : '—'}</td>
                    <td className="font-mono py-2 pl-2 text-right font-semibold tabular-nums text-data-sm">{!a.check_in ? <span className="text-warning-text">Not in</span> : a.check_out ? <span className="text-info-text">Checked out</span> : <span className="text-success-text">In</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ShowAllButton hidden={attList.hidden} all={attList.all} onToggle={attList.toggle} />
          </div>
        </section>
      )}

      {/* Area Sales Manager Team DSR (Point 5) */}
      <AsmTeamDsrCard />

      {/* The team's pipeline: lead velocity, stages, leaderboard, top deals. */}
      <SalesAdminDashboard />
    </div>
  );
}

export function DepartmentHeadDashboard() {
  const user = useUser();
  // This month's team figures for the banner (same query, same cache, as the
  // My team tab when it shows this month).
  const [monthFrom, monthTo] = PERIODS[2].range();
  const { data: monthTeam } = useQuery({
    queryKey: ['dashboard', 'team', monthFrom, monthTo],
    queryFn: async () => (await api.get<{ team: TeamRow[] | null; department: string }>('/api/dashboard-kpi/team', { params: { from: monthFrom, to: monthTo } })).data,
  });
  // Tabs: 'mine' (personal work), 'team' (team scoreboard & numbers), 'dsr' (team daily sales report), 'gps' (live fleet radar)
  const [tab, setTab] = useRememberedState<'mine' | 'team' | 'dsr' | 'gps'>('dept-head-dashboard', 'tab', 'mine');
  const [selectedRep, setSelectedRep] = useState<{ id: string; name: string } | null>(null);

  const choose = (t: 'mine' | 'team' | 'dsr' | 'gps') => {
    setTab(t);
    setSelectedRep(null);
  };

  const btn = (t: 'mine' | 'team' | 'dsr' | 'gps', label: string, Icon: typeof Users) => (
    <button type="button" role="tab" aria-selected={tab === t} onClick={() => choose(t)}
      className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-body-md font-semibold transition ${tab === t ? 'bg-primary text-on-primary shadow-xs' : 'text-on-surface-variant hover:bg-surface-variant'}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  return (
    <div className="space-y-4">
      <WelcomeBanner
        subtitle={<>Your {monthTeam?.department || user?.department || ''} team · <b className="font-semibold text-on-surface">this month</b>{monthTeam?.team ? ` · ${monthTeam.team.length} member${monthTeam.team.length === 1 ? '' : 's'}` : ''}</>}
        stats={monthTeam?.team ? teamStats(sumTeam(monthTeam.team)) : []}
      >
        <div role="tablist" aria-label="Dashboard" className="inline-flex flex-wrap rounded-lg border border-border bg-card p-1 shadow-xs">
          {btn('mine', 'My work', UserRound)}
          {btn('team', 'My team', Users)}
          {btn('dsr', 'Team DSR', FileText)}
          {btn('gps', 'Live GPS Radar', Radio)}
        </div>
      </WelcomeBanner>

      {tab === 'mine' && <SalesEmployeeDashboard embedded />}
      {tab === 'team' && <TeamView />}
      {tab === 'dsr' && (
        <div className="space-y-4">
          <AsmTeamDsrCard onSelectRep={(id, name) => setSelectedRep({ id, name })} />
          {selectedRep && (
            <div className="rounded-card border-2 border-primary/30 bg-primary-container/60 p-2 shadow-xs">
              <div className="flex items-center justify-between p-2">
                <span className="text-body-sm font-semibold text-primary-dim">
                  Viewing detailed DSR for: {selectedRep.name}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedRep(null)}
                  className="rounded-lg bg-card px-2.5 py-1 text-body-sm font-semibold text-on-surface-variant shadow-xs hover:bg-surface-variant"
                >
                  Close Detail View
                </button>
              </div>
              <BdmDsrWidget userId={selectedRep.id} repName={selectedRep.name} />
            </div>
          )}
        </div>
      )}
      {tab === 'gps' && (
        <div className="rounded-card border border-border bg-card p-2 shadow-xs overflow-hidden">
          <LiveFleetRadarPage />
        </div>
      )}
    </div>
  );
}
